import { CellWithPopover, Grade, HeaderWithPopover } from '../components';
import { Box, Flex, Text, Tooltip } from '@chakra-ui/react';
import { Column, createColumnHelper } from '@tanstack/react-table';
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { SortingIcon } from 'libs/shared/components/src';
import camelCase from 'lodash-es/camelCase';
import mapKeys from 'lodash-es/mapKeys';
import { LevelEnumType, SortEnum } from '@lon/shared/requests';
import { SsStandardsAggregationTypeEnum } from '@lon/shared/requests';
import { parseJSON } from '@lon/shared/utils';
import { filterNullable } from '@lon/suit/utils';
import {
  DistrictRecord,
  GradeRecord,
  Order,
  ReponseObject,
  SchoolRecord,
  StandardRecord,
  StudentRecord,
  TeacherRecord,
} from './types';

export const aggregateByOptions: Record<
  SsStandardsAggregationTypeEnum,
  string[]
> = {
  [SsStandardsAggregationTypeEnum.Student]: [
    'studentId',
    'studentName',
    'studentUsername',
  ],
  [SsStandardsAggregationTypeEnum.Teacher]: ['teacherId', 'teacherName'],
  [SsStandardsAggregationTypeEnum.Grade]: ['gradeLevel', 'gradeDisplayName'],
  [SsStandardsAggregationTypeEnum.School]: ['schoolId', 'schoolName'],
  [SsStandardsAggregationTypeEnum.District]: ['districtId', 'districtName'],
};

export const aggregateByOptionTitles: Record<
  SsStandardsAggregationTypeEnum,
  string
> = {
  [SsStandardsAggregationTypeEnum.Student]: 'Student',
  [SsStandardsAggregationTypeEnum.Teacher]: 'Teacher',
  [SsStandardsAggregationTypeEnum.Grade]: 'Grade',
  [SsStandardsAggregationTypeEnum.School]: 'Schools',
  [SsStandardsAggregationTypeEnum.District]: 'District',
};

type AggregateByTypeMap = {
  [SsStandardsAggregationTypeEnum.Student]: StudentRecord;
  [SsStandardsAggregationTypeEnum.Teacher]: TeacherRecord;
  [SsStandardsAggregationTypeEnum.Grade]: GradeRecord;
  [SsStandardsAggregationTypeEnum.School]: SchoolRecord;
  [SsStandardsAggregationTypeEnum.District]: DistrictRecord;
};

export const getColumns = <T extends keyof AggregateByTypeMap>(
  data: { standard: string; description?: string }[] = [],
  aggregateBy: T,
  isleaderSuit = false
) => {
  const columnHelper = createColumnHelper<AggregateByTypeMap[T]>();
  const accessorKeys = aggregateByOptions[aggregateBy];
  const title = aggregateByOptionTitles[aggregateBy];

  return [
    columnHelper.accessor(accessorKeys[1] as any, {
      cell: (info) => {
        const data = info?.row.original;
        return (
          <Box className="notranslate" py={2}>
            <Text as="span" variant="s2">
              {info.getValue() as string}
            </Text>
            {aggregateBy === SsStandardsAggregationTypeEnum.Student && (
              <Text variant="s2">{data[accessorKeys[2]] as string}</Text>
            )}
          </Box>
        );
      },
      header: (data) => (
        <Flex minW="265px">
          <Text as="span">{title as string}</Text>
          <Tooltip
            label={getSortLabel(data.column as any, aggregateBy, isleaderSuit)}
            variant="dark"
          >
            <Flex>
              <SortingIcon
                column={data?.column as any}
                onIconClick={data?.column.getToggleSortingHandler() as any}
              />
            </Flex>
          </Tooltip>
        </Flex>
      ),
    }),
    ...(data.map((item) => {
      return columnHelper.accessor(item.standard as any, {
        id: item.standard,
        cell: (info) => {
          const score = (info.row.original?.[item.standard] as StandardRecord)
            ?.scorePct;
          const grade =
            typeof score === 'undefined' || score === null
              ? undefined
              : Math.round(Number(score));

          return (
            <CellWithPopover
              item={item}
              info={info}
              grade={grade}
              aggregateBy={aggregateBy}
            >
              <Flex justify="center">
                <Grade grade={grade} />
              </Flex>
            </CellWithPopover>
          );
        },
        header: (headerData) => {
          return (
            <HeaderWithPopover
              item={item}
              headerData={headerData}
              sortTooltipLabel={getSortLabel(
                headerData.column as any,
                undefined,
                isleaderSuit
              )}
            />
          );
        },
      });
    }) || []),
  ];
};

export const prepareData = (
  data: ReponseObject | undefined,
  firstElement:
    | {
        VarCharValue: string;
      }[]
    | undefined
): StandardRecord[] => {
  const preparedData = data?.Rows?.map((row) => {
    return row.Data?.reduce<Record<string, string>>((acc, val, idx) => {
      const camelCaseKey = camelCase(firstElement?.[idx]?.VarCharValue);

      const value = val.VarCharValue;

      if (typeof value === 'undefined') {
        return acc;
      }

      return {
        ...acc,
        [camelCaseKey]: value,
      };
    }, {});
  }).filter(filterNullable);

  return preparedData as StandardRecord[];
};

interface DetailedLevelData {
  activity_id_list: string[];
  assignment_id_list: string[];
  score_pct: number;
  standard_description_list: { standard: string; description: string }[];
  standard_level_filter: string;
}

export const mapStandardToStudent = <T extends keyof AggregateByTypeMap>(
  data: StandardRecord[],
  aggregateBy: T
): AggregateByTypeMap[T][] => {
  const keys = aggregateByOptions[aggregateBy] as (keyof StandardRecord)[];

  return data?.reduce<AggregateByTypeMap[T][]>((acc, val) => {
    // Dynamically extract ID and name fields
    const baseFields = keys.reduce((obj, key) => {
      obj[key] = val[key];
      return obj;
    }, {} as any);

    const detailedLevelData = parseJSON<DetailedLevelData[]>(
      val.detailedLevelData || '[]'
    );

    // Find index by the first key (usually the ID)
    const idKey = keys[0];
    const itemIndex = acc.findIndex((item) => item[idKey] === val[idKey]);

    if (!Array.isArray(detailedLevelData)) {
      return acc;
    }

    const standardMap = detailedLevelData?.reduce((acc, val) => {
      const camelCaseObj = mapKeys(val, (v, k) => camelCase(k));
      const standardLevelFilter = camelCaseObj.standardLevelFilter as string;

      if (!standardLevelFilter) {
        return acc;
      }

      return {
        ...acc,
        [standardLevelFilter]: {
          ...camelCaseObj,
          standard: standardLevelFilter,
          description: (
            camelCaseObj?.standardDescriptionList as {
              standard: string;
              description: string;
            }[]
          )?.find((item) => item.standard === standardLevelFilter)?.description,
        },
      };
    }, {});

    if (itemIndex === -1) {
      acc.push({
        ...baseFields,
        ...standardMap,
      });
    } else {
      acc[itemIndex] = {
        ...acc[itemIndex],
        ...standardMap,
      };
    }

    return acc;
  }, []);
};

export const getOrder = (
  sorting: {
    id: string;
    desc: boolean;
  }[],
  aggregateBy: SsStandardsAggregationTypeEnum
): Order => {
  const sortingKey = aggregateByOptions[aggregateBy][1];

  return sorting.length && sorting[0]?.id === sortingKey
    ? {
        [sortingKey]: sorting[0].desc ? SortEnum.Desc : SortEnum.Asc,
      }
    : undefined;
};

export const isValidEnumKey = (
  key: string | undefined = ''
): key is keyof typeof LevelEnumType => {
  return key in LevelEnumType;
};

export const sortStandardsAlphaNumerically = (
  standards: {
    standard: string;
    description?: string | undefined;
  }[] = []
) => {
  return standards.sort((a, b) => {
    const regex = /(\d+|\D+)/g;
    const partsA = a?.standard.match(regex) || [];
    const partsB = b?.standard.match(regex) || [];

    // compare number parts and string parts of standards
    for (let i = 0; i < Math.max(partsA.length, partsB.length); i++) {
      const partA = partsA[i] || '';
      const partB = partsB[i] || '';

      const numA = parseInt(partA, 10);
      const numB = parseInt(partB, 10);

      if (!isNaN(numA) && !isNaN(numB)) {
        if (numA !== numB) {
          return numA - numB;
        }
      } else {
        if (partA !== partB) {
          return partA.localeCompare(partB);
        }
      }
    }

    return 0;
  });
};

export const getEnumKeyFromNumber = (
  num: number
): keyof typeof LevelEnumType => {
  const key = `Level${num}` as keyof typeof LevelEnumType;

  if (key in LevelEnumType) {
    return key;
  }

  // the default level is Level5
  return 'Level5';
};

export const getSortLabel = (
  column: Column<Record<any, any>>,
  aggregateBy?: SsStandardsAggregationTypeEnum,
  isLeaderSuit?: boolean
) => {
  if (!column.getCanSort() || !isLeaderSuit) {
    return '';
  }

  if ((column.id as string).includes('Name') && aggregateBy) {
    const title = aggregateByOptionTitles[aggregateBy];
    const order = column.getIsSorted()
      ? column.getIsSorted() === 'asc'
        ? 'acsending'
        : 'default'
      : 'descending';

    return `Sort data by ${title} in ${order} order`;
  }

  const order = column.getIsSorted()
    ? column.getIsSorted() === 'asc'
      ? 'default'
      : 'descending'
    : 'ascending';

  return `Sort data by grades for standard ${column.id} in ${order} order`;
};
